import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { prop } from 'ramda'
import { useSelector } from 'react-redux'
import { CLUSTER_HOST_SCHEDULING } from 'app/constants'
import { troubleshootingHostsDoc } from 'app/docLinks'
import ResourceUsageIndicator from 'app/plugins/infrastructure/components/common/ResourceUsageIndicator'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Badge from 'core/elements/badge'
import { BadgeVariant } from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { capitalizeString, formatDate } from 'utils/misc'
import { convertToTitleCase } from '../blueprint/helpers'
import { errorStatuses, healthyStatuses, pendingStatuses, unknownStatuses } from '../selectors'
import { IHostDetailsPageTabs } from './host-details/model'
import { VMHABannerState } from './vmha/types'

export const getIconOrBubbleColor = (status, theme: Theme) =>
  ({
    ok: theme.palette.green.main,
    failed: theme.palette.red.main,
  })[status] || theme.palette.grey[500]

export const useStyles = makeStyles<Theme>((theme) => ({
  hostName: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  circle: {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap',
    '&:before': {
      content: '""',
      display: 'inherit',
      height: 12,
      width: 12,
      borderRadius: '50%',
      backgroundColor: getIconOrBubbleColor('ok', theme),
    },
  },
  offline: {
    '&:before': {
      backgroundColor: theme.palette.red.main,
    },
  },
  missingPackages: {
    color: theme.palette.yellow.main,
    marginRight: 6,
  },
  warning: {
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 12,
    color: getIconOrBubbleColor('ok', theme),
  },
  offlineIcon: {
    color: theme.palette.red.main,
  },
  dot: {
    fontSize: 12,
    marginRight: 4,
  },
  green: {
    color: theme.palette.green.main,
  },
  yellow: {
    color: theme.palette.yellow.main,
  },
  red: {
    color: theme.palette.red.main,
  },
  orange: {
    color: theme.palette.orange.main,
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  ipAddressRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    gap: theme.spacing(1),
  },
  vmhaInProgress: {
    color: theme.palette.red[500],

    '& i': {
      color: theme.palette.red[500],
    },
  },
  vmhaWarning: {
    color: theme.palette.yellow[500],
  },
}))

export const getSearchFunctions = (isVmware) => [
  {
    property: 'roleStatuses',
    searchFn: (value, searchString) => {
      return value.some((role) => {
        return (
          role?.display?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          role?.status?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
  {
    property: 'schedulingStatus',
    searchFn: (value, searchString, item) => {
      const underMaintenance = !!item?.hypervisor?.service?.disabled_reason
      return (
        value?.toLowerCase?.().includes(searchString.toLowerCase()) ||
        (underMaintenance && 'Maintenance Mode'.toLowerCase().includes(searchString.toLowerCase()))
      )
    },
  },
  {
    property: 'interfaces',
    searchFn: (value, searchString) => {
      return value.some((iface) => {
        return (
          iface?.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          iface?.ip?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
]

export const IPAddressCellComponent = ({ value }) => {
  const classes = useStyles()
  return (
    <div>
      {value?.map((iface) => (
        <div key={iface.ip} className={classes.ipAddressRow}>
          <Text variant="body2" component="div">
            {iface.ip?.split('%')?.[0]}
          </Text>
          <Text variant="body2" component="div">
            {iface.name}
          </Text>
        </div>
      ))}
    </div>
  )
}

export const RespondingCell = ({ value, item }) => {
  const classes = useStyles()
  const isOnline = value === 'online'
  return (
    <div>
      <Text variant="body2" className={classes.nowrap}>
        <FontAwesomeIcon
          className={clsx(
            classes.icon,
            { [classes.offlineIcon]: !isOnline, [classes.offline]: !isOnline },
            classes.circle,
          )}
          solid
        >
          circle
        </FontAwesomeIcon>
        {value === 'offline' ? (
          <ExternalLink url={troubleshootingHostsDoc}>{capitalizeString(value)}</ExternalLink>
        ) : (
          capitalizeString(value)
        )}
      </Text>
      {value === 'offline' && item?.lastResponseTime && (
        <Text variant="body2">(since {formatDate(`${item.lastResponseTime}Z`)})</Text>
      )}
    </div>
  )
}

export const NameWithVMHACell = ({ value, item, vmhaStatuses }: any) => {
  const classes = useStyles()
  const vmhaStatus = vmhaStatuses?.get(item?.id)
  // Check if host has roles
  const hasRoles = item?.v1Roles?.length > 0
  // Show the VMHA banner only when the host is not responding
  const isVMHAEventCompletedWithHostOnline =
    item?.responding === 'online' && vmhaStatus?.state === VMHABannerState.Completed
  const hasVMHA = !!vmhaStatus && !isVMHAEventCompletedWithHostOnline && hasRoles
  const isInProgress = vmhaStatus?.state === VMHABannerState.InProgress

  const getStatusClass = (phase: VMHABannerState): string => {
    switch (phase) {
      case VMHABannerState.InProgress:
      case VMHABannerState.Error:
        return classes.vmhaInProgress
      case VMHABannerState.Warning:
        return classes.vmhaWarning
      case VMHABannerState.Completed:
      default:
        return ''
    }
  }

  const getBadgeVariant = (phase: VMHABannerState): BadgeVariant => {
    switch (phase) {
      case VMHABannerState.Completed:
        return 'default'
      case VMHABannerState.InProgress:
      case VMHABannerState.Error:
        return 'error'
      case VMHABannerState.Warning:
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <div className={classes.hostName}>
      <SimpleLink
        src={routeManager.routes.openstack.hostDetails.path({
          id: item?.id,
          tab: IHostDetailsPageTabs.Overview,
        })}
      >
        {value}
      </SimpleLink>
      {hasVMHA && (
        <Badge
          variant={getBadgeVariant(vmhaStatus?.state)}
          text={
            <Text variant="caption2" className={getStatusClass(vmhaStatus?.state)}>
              {isInProgress ? (
                <>
                  <FontAwesomeIcon size="md" solid spin>
                    sync
                  </FontAwesomeIcon>{' '}
                  VMHA
                </>
              ) : (
                `VMHA ${
                  vmhaStatus?.state === VMHABannerState.Error
                    ? 'Error'
                    : vmhaStatus?.state === VMHABannerState.Warning
                      ? 'Warning'
                      : vmhaStatus?.state === VMHABannerState.Completed
                        ? 'Completed'
                        : ''
                }`
              )}
            </Text>
          }
        />
      )}
    </div>
  )
}

const uberRoleMap = {
  hypervisor: [
    'pf9-neutron-base',
    'pf9-neutron-ovn-controller',
    'pf9-neutron-ovn-metadata-agent',
    'pf9-ostackhost-neutron',
    'pf9-ip-discovery',
  ],
  'persistent-storage': ['pf9-cindervolume-base', 'pf9-cindervolume-config'],
  'image-library': ['pf9-glance-role'],
  dns: ['pf9-designate'],
}

export const WWNCellComponent = ({ value }) => {
  return (
    <div>
      {value?.map((item) => (
        <Tooltip
          key={item.host}
          message={
            <>
              {Object.keys(item).map((key) => (
                <div key={key}>
                  <strong>{convertToTitleCase(key)}:</strong> {item[key]}
                </div>
              ))}
            </>
          }
        >
          {item.host} <FontAwesomeIcon size="sm">info-circle</FontAwesomeIcon>
        </Tooltip>
      ))}
    </div>
  )
}

export const RoleStatusesCell = ({ value, item, setUberRoleFailedInfo = null }) => {
  const classes = useStyles()
  const roles = item?.v1Roles
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  if (!roles?.length) {
    return <Text variant="body2">Unauthorized</Text>
  }
  return (
    <div>
      {value.map((role) => {
        const subRoles = uberRoleMap[role.role]
        const isUberRole = !!subRoles
        return (
          <Text variant="body2" className={classes.nowrap} key={role.role}>
            <FontAwesomeIcon
              className={clsx({
                [classes.dot]: true,
                [classes.green]: healthyStatuses.includes(role.status),
                [classes.orange]: unknownStatuses.includes(role.status),
                [classes.yellow]: pendingStatuses.includes(role.status),
                [classes.red]: errorStatuses.includes(role.status),
              })}
              solid
            >
              circle
            </FontAwesomeIcon>
            {role.display} -{' '}
            {errorStatuses.includes(role.status) ? (
              isUberRole && !!setUberRoleFailedInfo ? (
                <SimpleLink onClick={() => setUberRoleFailedInfo({ host: item, role, subRoles })}>
                  {role.status}
                </SimpleLink>
              ) : (
                <ExternalLink url={troubleshootingHostsDoc}>{role.status}</ExternalLink>
              )
            ) : (
              role.status
            )}
          </Text>
        )
      })}
      {isVmware && item?.vmwareRolesIncomplete && (
        <div className={classes.warning}>
          <FontAwesomeIcon className={classes.missingPackages}>
            triangle-exclamation
          </FontAwesomeIcon>
          <Text variant="body2">vSphere Gateway Authorization Incomplete</Text>
        </div>
      )}
    </div>
  )
}

export const ResourceUsageIndicators = ({
  usageStats,
  allocationStats,
  usageValueConverter = null,
  allocationValueConverter = null,
}) => {
  return (
    <>
      <ResourceUsageIndicator
        valueConverter={usageValueConverter?.fn}
        units={usageValueConverter?.units || usageStats?.units}
        label="Used"
        stats={usageStats}
      />
      <ResourceUsageIndicator
        valueConverter={allocationValueConverter?.fn}
        units={allocationValueConverter?.units || allocationStats?.units}
        label="Allocated"
        stats={allocationStats}
      />
    </>
  )
}

export const SchedulingStatusCell = (value, item) => {
  if (!item.hypervisor) {
    return 'N/A'
  }
  return (
    <div>
      <Badge
        variant={value === CLUSTER_HOST_SCHEDULING.enabled ? 'success' : 'warning'}
        text={value}
      />
      {item?.hypervisor?.service?.disabled_reason && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Badge
            variant="warning"
            text={
              item?.hypervisor?.running_vms > 0
                ? 'Maintenance Mode in Progress'
                : 'Maintenance Mode'
            }
            ellipsisAt={30}
            tooltipBody={
              item?.hypervisor?.running_vms > 0
                ? 'Migrations may be in-progress, or may need manual intervention to migrate remaining active VMs. Please check maintenance mode progress within host details.'
                : null
            }
          />
        </div>
      )}
    </div>
  )
}
